import { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCarbonFootprint } from './useCarbonFootprint';
import { FaLocationCrosshairs, FaRegClock, FaMapLocationDot } from "react-icons/fa6";
import { useConsent } from '../../../../context/ConsentContext';
import '../../../../css/DppWidget.css';

const CarbonFootprintWidget = ({ submodel }) => {
  const intl = useIntl();
  const [mapConsented, setMapConsented] = useState(false);
  const { consent } = useConsent();

  const {
    scaledPCF,
    scaledTCF,
    actualQuantity,
    deliveryAddress,
    mapUrl,
    transportStepsCount,
    publicationDate
  } = useCarbonFootprint(submodel);

  return (
    <div className="dpp-widget">
      <Row className="g-0">
        {/* Left side – data */}
        <Col lg={12} xl={6} className="p-4 p-xl-5">
          <h2 className="dpp-widget-title">
            <FormattedMessage
              id="carbonFootprint.title"
              defaultMessage="Recent total carbon footprint"
            />
          </h2>

          <Row className="g-4 mb-5">
            <Col sm={6}>
              <div className="dpp-widget-value">
                {scaledPCF.total.toFixed(2)} kg<br />CO₂eq
              </div>
              <div className="dpp-widget-label">
                <FormattedMessage
                  id="carbonFootprint.pcf"
                  defaultMessage="Product Carbon Footprint"
                />
              </div>
            </Col>

            <Col sm={6}>
              <div className="dpp-widget-value">
                {scaledTCF.total.toFixed(2)} kg<br />CO₂eq
              </div>
              <div className="dpp-widget-label">
                <FormattedMessage
                  id="carbonFootprint.tcf"
                  defaultMessage="Transport Carbon Footprint"
                />
              </div>
            </Col>
          </Row>

          {/* Scaled quantity info */}
          {actualQuantity !== 1 && (
            <div className="alert alert-info py-2 px-3 mb-4" role="alert">
              <FormattedMessage
                id="carbonFootprint.scaledFor"
                defaultMessage="Scaled for {count} item{count, plural, one {} other {s}}"
                values={{ count: actualQuantity }}
              />
            </div>
          )}

          {/* Delivery address box */}
          {deliveryAddress && (
            <div className="dpp-widget-info">
              <small className="text-uppercase fw-bold d-blockmb-3">
                <FaLocationCrosshairs style={{position: 'relative', top: '-2px', marginRight: '6px'}}/>
                <FormattedMessage
                  id="carbonFootprint.lastHandover"
                  defaultMessage="Last Transport Handover"
                />
              </small>
              <div className="fw-medium mt-2">{deliveryAddress}</div>
              <small className="text-muted d-block mt-2">
                <FaRegClock style={{position: 'relative', top: '-2px', marginRight: '6px', fontSize: '12px'}}/>
                {publicationDate} 
                {transportStepsCount > 0 && (
                  <>
                    {' • '}
                    <FormattedMessage
                      id="carbonFootprint.transportSteps"
                      defaultMessage="{count} transport steps total"
                      values={{ count: transportStepsCount }}
                    />
                  </>
                )}
              </small>
            </div>
          )}
        </Col>

        {/* Right side – map */}
        <Col lg={12} xl={6} className="p-0">
          {mapUrl ? (
            <div className="dpp-widget-map-container">
              {mapConsented && consent !== 'declined' ? (
                <iframe
                  title={intl.formatMessage({
                    id: 'carbonFootprint.mapTitle',
                    defaultMessage: 'Delivery location',
                  })}
                  src={mapUrl}
                  allowFullScreen
                  loading="lazy"
                />
              ) : (
                <div className="dpp-widget-map-consent">
                  <FaMapLocationDot className="dpp-widget-map-consent-icon" />
                  <p className="dpp-widget-map-consent-text">
                    {consent === 'declined' ? (
                      <FormattedMessage
                        id="carbonFootprint.mapConsentDeclined"
                        defaultMessage="Map disabled. You declined cookies — update your preference to enable it."
                      />
                    ) : (
                      <FormattedMessage
                        id="carbonFootprint.mapConsentText"
                        defaultMessage="This map is provided by Google Maps. Loading it shares your IP address with Google."
                      />
                    )}
                  </p>
                  {consent !== 'declined' && (
                    <button
                      className="dpp-widget-map-consent-btn"
                      onClick={() => setMapConsented(true)}
                    >
                      <FormattedMessage
                        id="carbonFootprint.mapConsentBtn"
                        defaultMessage="Show map"
                      />
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 bg-light text-muted">
              <FormattedMessage
                id="carbonFootprint.noLocation"
                defaultMessage="No location available"
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CarbonFootprintWidget;